import "./App.css";
import "./index.css";
import "@fontsource/poppins";
import { Route, Routes } from "react-router-dom";
import { Suspense, lazy } from "react";
import "./utils/i18n";
import { MuiThemeProvider } from "./providers/MuiThemeProvider";

// Wrap all imports with React.lazy
const GetStarted = lazy(() => import("./pages/GetStarted"));
const UserStages = lazy(() => import("./pages/User/UserStages"));
const SignIn = lazy(() => import("./pages/SignIn"));
const SignUp = lazy(() => import("./pages/SignUp"));
const Phone = lazy(() => import("./pages/Phone"));
const Success = lazy(() => import("./pages/Success"));
const UserAddStages = lazy(() => import("./pages/User/UserAddStages"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Organizations = lazy(() => import("./pages/Organizations"));
const Home = lazy(() => import("./pages/Home"));
const CommunityTabs = lazy(() => import("./pages/CommunityTabs"));
const RouteCompletion = lazy(() => import("./pages/RouteCompletion"));
const Review = lazy(() => import("./pages/Review"));
const ReviewsBox = lazy(() => import("./pages/ReviewsBox"));
const EditProfile = lazy(() => import("./pages/EditProfile"));
const Settings = lazy(() => import("./pages/Settings"));
const Profile = lazy(() => import("./pages/Profile"));
const AdminLogin = lazy(() => import("./pages/Admin/AdminLogin"));
const Dashboard = lazy(() => import("./pages/Admin/Dashboard"));
const AdminNotifications = lazy(
  () => import("./pages/Admin/AdminNotifications"),
);
const UserManagement = lazy(() => import("./pages/Admin/UserManagement"));
const AdminRouteDetails = lazy(() => import("./pages/Admin/AdminRouteDetails"));
const ChatInbox = lazy(() => import("./pages/ChatInbox"));
const MainLayout = lazy(() => import("./pages/MainLayout"));
const NotificationsBox = lazy(() => import("./pages/NotificationsBox"));
const UserDashboard = lazy(() => import("./pages/User/UserDashboard"));
const UserRoutes = lazy(() => import("./pages/User/UserRoutes"));
const AdminLayout = lazy(() => import("./pages/Admin/AdminLayout"));
const UserLayout = lazy(() => import("./pages/User/UserLayout"));
const UserNotifications = lazy(() => import("./pages/User/UserNotifications"));
const UserNotificationsSettings = lazy(
  () => import("./pages/User/UserNotificationsSettings"),
);
const Location = lazy(() => import("./pages/Location"));
const ForgetPassword = lazy(() => import("./components/ForgetPassword"));
const OTPPage = lazy(() => import("./components/OTPPage"));
const OrganizationalDetails = lazy(() => import("./pages/OrganizationDetails"));
const AdminRoutes = lazy(() => import("./pages/Admin/AdminRoutes"));
const AdminCreateRoute = lazy(() => import("./pages/Admin/AdminCreateRoute"));
const AdminPlaces = lazy(() => import("./pages/Admin/AdminPlaces"));
const AdminAddPlaces = lazy(() => import("./pages/Admin/AdminAddPlaces"));
const AdminPlaceDetail = lazy(() => import("./pages/Admin/AdminPlaceDetail"));
const AdminNotificationsSettings = lazy(
  () => import("./pages/Admin/AdminNotificationsSettings"),
);
const Channels = lazy(() => import("./pages/Admin/Channels"));
const ChannelForm = lazy(() => import("./pages/Admin/ChannelForm"));
const CommunityDetails = lazy(() => import("./pages/CommunityDetails"));
const OtpVerification = lazy(() => import("./pages/OtpVerification"));
const PlaceListings = lazy(() => import("./pages/User/PlaceListings"));
const ListingDetails = lazy(() => import("./pages/User/ListingDetails"));
const AdminProfile = lazy(() => import("./pages/Admin/AdminProfile"));
const NoPageFound = lazy(() => import("./pages/NoPageFound"));
const ProtectedLayout = lazy(() => import("./pages/User/ProtectedLayout"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const PrivateMessage = lazy(() => import("./pages/PrivateMessage"));
const OfflineNavigation = lazy(() => import("./pages/OfflineNavigation"));
const SharePlace = lazy(() => import("./pages/SharePlace"));
const AdminUpdateRoute = lazy(() => import("./pages/AdminUpdateRoute"));
const Downloads = lazy(() => import("./pages/Downloads"));
const LoadingScreen = lazy(() => import("./components/LoadingScreen"));
const OfflineHandler = lazy(() => import("./components/OfflineHandler"));
const TopDownloadIconAnimation = lazy(
  () => import("./components/TopDownloadIconAnimation"),
);
const ReportsPage = lazy(() => import("./pages/ReportsPage"));
const ReportDetailsPage = lazy(() => import("./pages/ReportsDetailsPage"));
const VolunteerApplications = lazy(
  () => import("./components/VolunteerApplications")
);

import AdminMapMessagesPage from "./pages/AdminMapMessagesPage";
import AdminPostCommentsPage from "./pages/AdminPostCommentsPage";
import AdminReviewPage from "./pages/AdminReviewPage";
import AppReviewsPage from "./pages/AppReviewsPage";

// CMP CMP CMP
function App() {
  // VARS

  // FUNCTIONS

  // JSX JSX JSX
  return (
    <div>
      <OfflineHandler>
        <TopDownloadIconAnimation>
          <Suspense fallback={<LoadingScreen />}>
            <MuiThemeProvider>
              <Routes>
                <Route path="*" element={<NoPageFound />} />
                <Route path="/" element={<GetStarted />} />

                {/* ========================================= */}
                {/* 1. VISUAL WRAPPER: Gets the App Menu      */}
                {/* ========================================= */}
                <Route element={<MainLayout />}>
                  
                  {/* PUBLIC PAGES (With Menu) */}
                  <Route path="/home" element={<Home />} />
                  <Route path="/place-listings" element={<PlaceListings />} />
                  <Route path="/listing-details" element={<ListingDetails />} />
                  <Route
                    path="/share-place/:placeId"
                    element={<SharePlace />}
                  />
                  <Route path="/location" element={<Location />} />
                  <Route path="/downloads" element={<Downloads />} />

                  {/* PRIVATE PAGES (With Menu AND Security/Zoom) */}
                  <Route element={<ProtectedLayout />}>
                    <Route path="/community" element={<CommunityTabs />} />
                    <Route path="/details" element={<CommunityDetails />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/edit-profile" element={<EditProfile />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/reports" element={<ReportsPage />} />
                    <Route path="/reports/:id" element={<ReportDetailsPage />} />
                  </Route>

                </Route>

                {/* ========================================= */}
                {/* 2. PRIVATE PAGES WITHOUT APP MENU         */}
                {/* ========================================= */}
                <Route path="/" element={<ProtectedLayout />}>
                  <Route path="chat-inbox" element={<ChatInbox />} />
                  <Route path="private-messages" element={<PrivateMessage />} />
                  <Route path="privacy" element={<Privacy />} />
                  <Route path="terms" element={<Terms />} />
                  <Route path="organizations" element={<Organizations />} />
                  <Route
                    path="organization/:id"
                    element={<OrganizationalDetails />}
                  />
                  <Route
                    path="route-completion"
                    element={<RouteCompletion />}
                  />
                  <Route path="review" element={<Review />} />
                  <Route path="reviews-box" element={<ReviewsBox />} />
                  <Route
                    path="notifications-box"
                    element={<NotificationsBox />}
                  />
                </Route>

                <Route path="/forgot-password" element={<ForgetPassword />} />
                <Route path="/otp" element={<OTPPage />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/sign-in" element={<SignIn />} />
                <Route path="/sign-up" element={<SignUp />} />
                <Route path="/otp-verification" element={<OtpVerification />} />
                <Route path="/phone" element={<Phone />} />
                <Route path="success" element={<Success />} />

                {/* needs client confirmation */}
                <Route
                  path="/offline-navigation"
                  element={<OfflineNavigation />}
                />

                <Route path="/admin/" element={<AdminLogin />} />

                <Route path="/" element={<AdminLayout />}>
                  <Route path="/admin/dashboard" element={<Dashboard />} />
                  <Route
                    path="/admin/user-management"
                    element={<UserManagement />}
                  />
                  <Route path="/admin/routes" element={<AdminRoutes />} />
                  <Route
                    path="admin/routes/:id"
                    element={<AdminRouteDetails />}
                  />
                  <Route
                    path="/admin/create-route"
                    element={<AdminCreateRoute />}
                  />
                  <Route
                    path="/admin/update-route/:id"
                    element={<AdminUpdateRoute />}
                  />
                  <Route path="/admin/places" element={<AdminPlaces />} />
                  <Route
                    path="/admin/add-places"
                    element={<AdminAddPlaces />}
                  />

                  {/* not included */}
                  <Route
                    path="/admin/place/:id"
                    element={<AdminPlaceDetail />}
                  />

                  <Route path="/admin/stages" element={<UserStages />} />
                  <Route path="/admin/add-stages" element={<UserAddStages />} />
                  <Route
                    path="/admin/notifications"
                    element={<AdminNotifications />}
                  />
                  <Route path="/admin/profile" element={<AdminProfile />} />
                  <Route path="/admin/channel" element={<Channels />} />
                  <Route path="/admin/channel-form" element={<ChannelForm />} />

                  {/* not included */}
                  <Route
                    path="/admin/notifications/settings"
                    element={<AdminNotificationsSettings />}
                  />

                  <Route path="/admin/reports" element={<ReportsPage />} />

                  <Route
                    path="/admin/reports/:id"
                    element={<ReportDetailsPage />}
                  />

                  <Route
                    path="/admin/map-messages"
                    element={<AdminMapMessagesPage />}
                  />

                  <Route
                    path="/admin/post-comments"
                    element={<AdminPostCommentsPage />}
                  />

                  <Route path="/admin/reviews" element={<AdminReviewPage />} />

                  <Route
                    path="/admin/app-reviews"
                    element={<AppReviewsPage />}
                  />

                  <Route
                    path="/admin/volunteer-applications"
                    element={<VolunteerApplications />}
                  />
                </Route>

                <Route path="/user/login" element={<AdminLogin />} />

                {/* not part of application */}
                <Route element={<UserLayout />}>
                  <Route path="/user/routes" element={<UserRoutes />} />
                  <Route path="/user/dashboard" element={<UserDashboard />} />
                  <Route path="/user/places" element={<AdminPlaces />} />
                  <Route
                    path="/user/notifications"
                    element={<UserNotifications />}
                  />
                  <Route
                    path="/user/notifications/settings"
                    element={<UserNotificationsSettings />}
                  />
                </Route>
              </Routes>
            </MuiThemeProvider>
          </Suspense>
        </TopDownloadIconAnimation>
      </OfflineHandler>
    </div>
  );
}

export default App;
